/**
 * The shared state-machine and steering layer every behaviour is written
 * against.
 *
 * A behaviour never touches position or velocity. It decides a mode, a goal
 * point and a speed; the entity system integrates that once per frame with
 * collision, so no behaviour can walk a body through a wall or make it jitter.
 */

import { TUNING } from '../core/config';
import { hasLineOfSight } from '../core/grid';
import { clamp01, dist2 } from '../core/math';
import type {
  AudioAPI,
  EntityDef,
  EntityKind,
  EntityMode,
  EntityState,
  EventBus,
  GameContext,
  GeneratedLevel,
  Grid,
  PlayerState,
  RNG,
  Vec2,
} from '../core/types';
import { clearLine, type Navigator, type Path, type PropField } from './nav';

/** Everything a behaviour is allowed to read, rebuilt in place each frame. */
export interface EntityWorld {
  ctx: GameContext;
  /** Seconds since this agent last thought. Not necessarily the frame delta. */
  dt: number;
  grid: Grid;
  /**
   * The level's solid scenery, or null on a level without any. Steering routes
   * around it; sight and hearing deliberately do not, because a half-wall is
   * something you can see over and shout past but not walk through.
   */
  props: PropField | null;
  level: GeneratedLevel;
  player: PlayerState;
  events: EventBus;
  audio: AudioAPI;
  nav: Navigator;
  /** Shared stream, forked from the level seed. Deterministic. */
  rng: RNG;
  /** Light level under the player this frame, 0..1. Cached; it costs a raycast. */
  playerLight: number;
  /** Light level at a world point. */
  lightAt(pos: Vec2): number;
  /** Player sanity, 0..100. */
  sanity: number;
  /**
   * Report sanity pressure from this entity, in points per second. The survival
   * system owns the stat and applies it; behaviours only say how bad it is, so
   * a rate is reported rather than a delta and `w.dt` must not be folded in.
   */
  dread(state: EntityState, rate: number): void;

  /**
   * Tell every other live entity of `kind` where the player is, at `loudness`.
   *
   * The pack call, and the only way one behaviour may reach another. It writes
   * exactly what `EntitySystem.hearNoise` writes — a position and a loudness,
   * consumed by the other agent's next think tick — so nothing here can move a
   * body or hand anything certainty. A partygoer called across the floor still
   * has to walk there and still has to find the player at the end of it.
   *
   * `radius` is metres, or `Infinity` for the whole level. The caller is
   * excluded; a call it makes to itself would be a loop.
   */
  alertKin(from: EntityState, kind: EntityKind, loudness: number, radius: number): void;
}

/** Per-instance mutable state the shared code and the behaviours both use. */
export interface Agent {
  readonly state: EntityState;
  readonly def: EntityDef;
  readonly behavior: Behavior;
  readonly path: Path;
  /** Deterministic per-instance stream. */
  readonly rng: RNG;

  /** Seconds of un-thought time waiting for the scheduler. */
  thinkAccum: number;
  /** Seconds until the next A* repath is allowed. */
  repathCooldown: number;
  /** Seconds until this agent may attack again. */
  attackCooldown: number;

  /** A queued one-off noise, consumed by `think`. Storage is preallocated. */
  readonly heardPos: Vec2;
  heardPending: boolean;
  heardLoudness: number;
  /** Seconds left of the current search before it gives up. */
  searchTimer: number;

  /** Behaviour-local countdown: growl lead, mimic step, stare hold. */
  timer: number;
  /** Second behaviour-local countdown, for behaviours that need two clocks. */
  timer2: number;
  /** Behaviour-local counter: hollow look count, wander retries. */
  counter: number;
  /** Latches "the player is looking at me" so one stare counts once. */
  lookLatch: boolean;
  /** True once `entity:sighted` has fired for this instance. */
  sighted: boolean;
  /** True once this instance has been a real encounter (reached stalk or chase). */
  engaged: boolean;
  /**
   * True when this is the first of its kind the player has met this run.
   *
   * A behaviour whose counterplay cannot be worked out from a corpse uses this
   * to teach it once: the first hound of a run is given a longer, plainer
   * warning and says out loud what it is doing. Every one after it is at full
   * difficulty. The entity system owns the bookkeeping.
   */
  firstOfRun: boolean;

  /**
   * Where the behaviour wants to end up. Persistent across ticks; the steering
   * helpers never write it, so passing it to `moveTo` is safe.
   */
  readonly dest: Vec2;
  /** The immediate steering point, rewritten every tick by the helpers below. */
  readonly goal: Vec2;
  /** Metres per second it wants to move. 0 holds still. */
  goalSpeed: number;
  /** Yaw the body should turn toward. */
  desiredYaw: number;
  /** Radians per second it may turn. */
  turnRate: number;
  /** Point it drifts around while idle, so wanderers do not cross the level. */
  readonly anchor: Vec2;
  /** Wander state: standing still between legs. */
  resting: boolean;
  /** Seconds spent trying to move and getting nowhere. */
  stuck: number;
  /**
   * Recent stuck events, decaying. Two in quick succession means routing has
   * had its go and cannot fix this one.
   */
  snags: number;
  /**
   * Seconds left of ignoring scenery collision.
   *
   * The escape hatch of last resort. Every layer above this is meant to stop a
   * body ever needing it — the search refuses steps scenery closes off, the
   * steering slides, a snag throws the route away — but a monster that clips
   * through a chair for half a second is a much smaller failure than a monster
   * frozen against one in a doorway for the rest of the run.
   */
  propGrace: number;
  /** True while the agent is outside `activeRadius` and not being simulated. */
  asleep: boolean;
  /**
   * Seconds of licence to keep hunting from beyond `activeRadius`. Zero for
   * anything that is not actively hunting, which is most things most of the
   * time.
   *
   * The ordinary sleep rule exists because an entity the player has walked away
   * from should forget them — it zeroes awareness and the last known position.
   * That is right for a wanderer and wrong for something that has genuinely
   * picked the player up and set off, because on a folded level thirty metres
   * of corridor behind you is often forty-seven metres of straight line and the
   * rule would cancel the encounter mid-approach. So this raises that agent's
   * own thresholds, and only that agent's, for as long as it is hunting.
   *
   * Two things grant it. The entity system refreshes it every frame an agent of
   * a kind in `TUNING.entities.pursuit.kinds` spends in a hunting mode, and the
   * slide surge hands out a much longer one to what it summons from across the
   * floor (see `surgeHunt`). Nothing a behaviour does grants it directly, so a
   * kind that cannot arrive can never be licensed by forgetting a rule.
   *
   * It is a countdown rather than a flag on purpose: a licence that expires
   * cannot leak. The entity system ticks it down every frame, drops it the
   * moment an engagement resolves, and reverts to the ordinary radii the
   * instant it hits zero — at which point a hunter still out in the dark falls
   * asleep and forgets the player like anything else.
   */
  longHunt: number;
  /**
   * True while this agent is the one a slide surge summoned and is narrating.
   *
   * `longHunt` used to be the identity on its own, back when the surge was the
   * only thing that granted it. It is not any more — an ordinary hound that
   * commits to a chase now carries a licence too — so the surge would otherwise
   * mistake the nearest chasing anything for the thing it sent, and start
   * playing an approach cue track for something already in the room. Cleared
   * wherever `longHunt` is, so it still cannot outlive the hunt.
   */
  surgeHunt: boolean;
  /**
   * Policy, set once at spawn: metres out to which being looked at holds this
   * body absolutely still. Zero — the default — means it never freezes. The
   * faceling sets it, and nothing else does.
   *
   * It is declared here rather than decided inside the behaviour because the
   * freeze has to be exact. A behaviour ticks at 20 Hz; a player can turn
   * ninety degrees inside one of those ticks, and a fifty-millisecond lag is a
   * quarter of a metre of visible slide, which is the entire effect gone. So
   * the system evaluates the observation test every frame and writes `frozen`,
   * and the behaviour only says how far the rule reaches.
   *
   * A range and not a flag because the behaviour has to ask the same question
   * the system does, and the two answers must agree by construction. When they
   * did not, the faceling stalled at 1.7 m — outside its own attack range,
   * inside the range the system was still freezing it at — and stood there for
   * the rest of the run.
   */
  freezeRange: number;
  /**
   * Written by the entity system every frame. While true the body is not
   * integrated at all: no movement, no turning, and velocity is zeroed rather
   * than damped, so the stop is on the frame the player's view reaches it.
   */
  frozen: boolean;
  /** Set by a behaviour that wants to be removed cleanly. */
  retire: boolean;
}

/** One kind's decision-making. Pure policy; the system owns the body. */
export interface Behavior {
  readonly kind: EntityState['kind'];
  /** Called once, just after the instance is created. */
  spawn?(a: Agent, w: EntityWorld): void;
  /** Called on the agent's think tick while it is awake. */
  think(a: Agent, w: EntityWorld): void;
}

/** Metres past which a straight walk needs a real route. */
const DIRECT_RANGE2 = 3.0 * 3.0;
/** Squared distance at which a waypoint counts as reached. */
const ARRIVE2 = 0.55 * 0.55;
/** Squared goal drift that invalidates an existing route. */
const GOAL_DRIFT2 = 2.2 * 2.2;
/** Waypoints we may skip per think tick. Bounds the line-of-sight work. */
const MAX_SKIP = 2;

/* ------------------------------------------------------------------ *
 * Modes
 * ------------------------------------------------------------------ */

/**
 * Change mode and tell the rest of the game. Audio and UI hang off
 * `entity:mode`, so every transition must go through here.
 */
export function setMode(a: Agent, w: EntityWorld, mode: EntityMode): void {
  const s = a.state;
  if (s.mode === mode) return;
  const prev = s.mode;
  s.mode = mode;
  s.modeTime = 0;
  // What counts as "an encounter happened" for the director's cooldown. A
  // stationary hazard noticing you is not an encounter; something that can walk
  // over and take an interest in you is.
  if (mode === 'chase' || mode === 'attack' || (mode === 'stalk' && a.def.walkSpeed > 0)) a.engaged = true;
  w.events.emit('entity:mode', { id: s.id, kind: s.kind, mode, prev });
}

/* ------------------------------------------------------------------ *
 * Steering
 * ------------------------------------------------------------------ */

/** Point the body at a world position at a given speed. */
export function setGoal(a: Agent, x: number, z: number, speed: number): void {
  a.goal.x = x;
  a.goal.z = z;
  a.goalSpeed = speed;
  if (speed > 0) a.desiredYaw = Math.atan2(x - a.state.pos.x, -(z - a.state.pos.z));
}

/** Stop moving but keep whatever facing the behaviour asked for. */
export function holdStill(a: Agent): void {
  a.goal.x = a.state.pos.x;
  a.goal.z = a.state.pos.z;
  a.goalSpeed = 0;
}

/** Turn to face a point without moving toward it. */
export function faceToward(a: Agent, target: Vec2): void {
  a.desiredYaw = Math.atan2(target.x - a.state.pos.x, -(target.z - a.state.pos.z));
}

/**
 * Walk to `target`, routing around walls. Repaths at most every
 * `TUNING.entities.repathInterval` and follows a string-pulled route, so a
 * body never hugs a corner or oscillates between two waypoints.
 */
export function moveTo(a: Agent, w: EntityWorld, target: Vec2, speed: number): void {
  const s = a.state;
  const path = a.path;

  // Anything close and in plain sight is walked to directly; routing a body
  // three metres across an open room only makes it look robotic. Scenery in the
  // way puts it back on a route, which is the whole point of the props being
  // solid: a hound three metres away on the far side of a piano has to come
  // round the piano.
  if (dist2(s.pos, target) < DIRECT_RANGE2 && clearLine(w.grid, s.pos, target, a.def.radius, w.props)) {
    setGoal(a, target.x, target.z, speed);
    return;
  }

  const stale = !path.valid || path.index >= path.count || dist2(path.goal, target) > GOAL_DRIFT2;
  if (stale && !path.pending && a.repathCooldown <= 0) {
    w.nav.request(s.id, path, s.pos, target, a.def.radius);
    a.repathCooldown = TUNING.entities.repathInterval;
  }

  if (path.valid && path.index < path.count) {
    for (let i = 0; i < MAX_SKIP && path.index + 1 < path.count; i++) {
      if (!clearLine(w.grid, s.pos, path.pts[path.index + 1]!, a.def.radius, w.props)) break;
      path.index++;
    }
    const wp = path.pts[path.index]!;
    if (dist2(s.pos, wp) < ARRIVE2 && path.index + 1 < path.count) path.index++;
    const cur = path.pts[path.index]!;
    setGoal(a, cur.x, cur.z, speed);
    return;
  }

  // Still waiting on a route: creep toward the target rather than freeze.
  setGoal(a, target.x, target.z, speed * 0.55);
}

/** Move directly away from a point, sliding along whatever it backs into. */
export function retreatFrom(a: Agent, w: EntityWorld, from: Vec2, speed: number, look = true): void {
  const s = a.state;
  let dx = s.pos.x - from.x;
  let dz = s.pos.z - from.z;
  const l = Math.hypot(dx, dz);
  if (l < 1e-3) {
    dx = Math.sin(s.yaw);
    dz = -Math.cos(s.yaw);
  } else {
    dx /= l;
    dz /= l;
  }
  setGoal(a, s.pos.x + dx * 4, s.pos.z + dz * 4, speed);
  // Backing away while still facing the thing you are backing away from reads
  // as much more deliberate than turning tail.
  if (look) faceToward(a, from);
}

/* ------------------------------------------------------------------ *
 * Attacks and awareness
 * ------------------------------------------------------------------ */

/**
 * Land a hit if one is available. Damage goes out as `entity:attack` only: the
 * survival system listens for that and emits the `player:hurt` that everything
 * else keys off, so health has exactly one owner and the hurt event has exactly
 * one emitter. Emitting `player:hurt` here as well played the hit sound twice.
 */
export function tryAttack(a: Agent, w: EntityWorld): boolean {
  const def = a.def;
  if (def.damage <= 0 || a.attackCooldown > 0) return false;
  if (a.state.distToPlayer > def.attackRange) return false;
  if (!hasLineOfSight(w.grid, a.state.pos, w.player.pos)) return false;

  a.attackCooldown = def.attackCooldown;
  w.events.emit('entity:attack', { id: a.state.id, kind: a.state.kind, damage: def.damage });
  return true;
}

/** Raise awareness toward a signal, decaying when the signal is gone. */
export function driveAwareness(a: Agent, w: EntityWorld, signal: number, gain: number, forget: number): void {
  const s = a.state;
  if (signal > 0) {
    s.awareness = clamp01(s.awareness + signal * gain * w.dt);
    if (!s.lastKnown) s.lastKnown = { x: w.player.pos.x, z: w.player.pos.z };
    else {
      s.lastKnown.x = w.player.pos.x;
      s.lastKnown.z = w.player.pos.z;
    }
    a.searchTimer = TUNING.entities.searchDuration;
  } else {
    s.awareness = Math.max(0, s.awareness - forget * w.dt);
    a.searchTimer -= w.dt;
  }
}

/** Consume a queued one-off noise, if it was loud enough to be worth walking to. */
export function takeHeardNoise(a: Agent): Vec2 | null {
  if (!a.heardPending) return null;
  a.heardPending = false;
  if (a.heardLoudness < TUNING.entities.noiseThreshold) return null;
  return a.heardPos;
}

/** Add a stuck-detection sample; returns true when the body has given up. */
export function noteProgress(a: Agent, moved: number, dt: number): boolean {
  if (a.goalSpeed <= 0.01) {
    a.stuck = 0;
    return false;
  }
  if (moved > a.goalSpeed * dt * 0.25) {
    a.stuck = 0;
    return false;
  }
  a.stuck += dt;
  return a.stuck > 1.2;
}

/* ------------------------------------------------------------------ *
 * Position picking
 * ------------------------------------------------------------------ */

/**
 * A walkable point in an annulus around `origin`, or null. Tries a bounded
 * number of samples so a cramped level can never spin here.
 *
 * Pass `field` to reject spots standing inside scenery. It is worth doing for
 * anywhere a body will actually try to walk to and then wait: a wanderer that
 * picks the inside of a phone booth spends the next fifteen seconds grinding
 * against it.
 */
export function randomWalkableNear(
  grid: Grid,
  rng: RNG,
  origin: Vec2,
  minR: number,
  maxR: number,
  tries = 14,
  field: PropField | null = null,
  radius = 0.5,
): Vec2 | null {
  for (let i = 0; i < tries; i++) {
    const ang = rng.next() * Math.PI * 2;
    const r = minR + rng.next() * (maxR - minR);
    const x = origin.x + Math.cos(ang) * r;
    const z = origin.z + Math.sin(ang) * r;
    const c = grid.worldToCell(x, z);
    if (!grid.isWalkable(c.cx, c.cy)) continue;
    const spot = grid.cellToWorld(c.cx, c.cy);
    if (field && field.blocked(spot.x, spot.z, radius)) continue;
    return spot;
  }
  return null;
}

/**
 * The darkest walkable point within `radius`. Light-shy things use this to
 * choose where to stand and where to back off to.
 */
export function darkestNear(w: EntityWorld, origin: Vec2, radius: number, samples = 8): Vec2 | null {
  let best: Vec2 | null = null;
  let bestLight = Infinity;
  for (let i = 0; i < samples; i++) {
    const p = randomWalkableNear(w.grid, w.rng, origin, radius * 0.35, radius, 4, w.props);
    if (!p) continue;
    const l = w.lightAt(p);
    if (l < bestLight) {
      bestLight = l;
      best = p;
    }
  }
  return best;
}

/**
 * The brightest walkable point within `radius`, and the mirror of
 * `darkestNear`. One entity in the roster wants the opposite of the dark, and
 * it is the whole of that entity: a deathmoth with nothing better to do orbits
 * the nearest working fixture, which is what puts it in the lit rooms the
 * player is trying to rest in.
 */
export function brightestNear(w: EntityWorld, origin: Vec2, radius: number, samples = 8): Vec2 | null {
  let best: Vec2 | null = null;
  let bestLight = -1;
  for (let i = 0; i < samples; i++) {
    const p = randomWalkableNear(w.grid, w.rng, origin, radius * 0.35, radius, 4, w.props);
    if (!p) continue;
    const l = w.lightAt(p);
    if (l > bestLight) {
      bestLight = l;
      best = p;
    }
  }
  return best;
}

/**
 * A walkable point near `origin` from which the player cannot be seen. Used by
 * anything that would rather not be looked at.
 */
export function hiddenSpotNear(w: EntityWorld, origin: Vec2, radius: number, samples = 10): Vec2 | null {
  for (let i = 0; i < samples; i++) {
    const p = randomWalkableNear(w.grid, w.rng, origin, radius * 0.4, radius, 3, w.props);
    if (!p) continue;
    if (!hasLineOfSight(w.grid, p, w.player.pos)) return p;
  }
  return null;
}

/**
 * Drift around the anchor, stopping for a few seconds at each stop. The pauses
 * matter: something that walks without ever hesitating reads as a machine.
 * The anchor creeps toward each new destination so a wanderer slowly migrates
 * instead of orbiting one room forever.
 *
 * Nothing in the roster calls this at the moment — everything that used to has
 * either been retired or moved to `sweepStep`, which is the same shape with a
 * reason to go somewhere. It is kept because "drift about aimlessly" is the
 * obvious thing a future kind will want and it is not obvious to write: the
 * pauses, the migrating anchor and the scenery rejection are all load-bearing
 * and were all learned the hard way.
 */
export function wanderStep(a: Agent, w: EntityWorld, speed: number, radius = 16): void {
  a.timer2 -= w.dt;

  if (a.resting) {
    holdStill(a);
    if (a.timer2 > 0) return;
    a.resting = false;
    const target = randomWalkableNear(w.grid, a.rng, a.anchor, radius * 0.3, radius, 14, w.props, a.def.radius);
    if (target) {
      a.dest.x = target.x;
      a.dest.z = target.z;
      a.anchor.x += (target.x - a.anchor.x) * 0.35;
      a.anchor.z += (target.z - a.anchor.z) * 0.35;
    }
    a.timer2 = a.rng.range(8, 18);
    return;
  }

  if (a.timer2 <= 0 || dist2(a.state.pos, a.dest) <= ARRIVE2) {
    a.resting = true;
    a.timer2 = a.rng.range(1.6, 5.0);
    holdStill(a);
    return;
  }

  moveTo(a, w, a.dest, speed);
}

/**
 * Sweep the floor for the player. What a hunter does when it has no idea where
 * they are, in place of wandering.
 *
 * A spawn lands up to thirty-eight metres out, which is outside everything in
 * the roster's hearing at a walk. Before this existed, what happened next was a
 * coin flip: the thing drifted in a circle around wherever it appeared, the
 * player drifted somewhere else, and the encounter the director had spent two
 * and a half minutes of pressure on simply did not happen. That is the loudest
 * complaint this game has, and this is the fix.
 *
 * It is not a homing beacon and must never become one. Two things hold it back.
 * The sweep only updates while the player is making a noise the building could
 * plausibly carry — below `TUNING.entities.noiseThreshold` nothing hears you,
 * and a crouched player emits 0.08 against a threshold of 0.22 — so going quiet
 * stops it dead and the hunter falls back to drifting where it stands. And the
 * point it walks to carries an error proportional to the distance, so it
 * arrives in roughly the right *room* and its own ears have to finish the job.
 *
 * The result is the counterplay the hound has always had, applied to the
 * approach instead of only to the last thirty metres: keep walking and
 * something will find you; stop, crouch and wait, and it will not.
 *
 * `live` overrides what counts as a reason to sweep. It defaults to the
 * player's noise, which is right for everything that hunts by ear. The
 * deathmoth passes its own: a moth does not care how loudly you walk, it cares
 * whether your torch is on, and the switch on the torch is its counterplay in
 * exactly the way crouching is the hound's.
 */
export function sweepStep(a: Agent, w: EntityWorld, speed: number, live?: boolean): void {
  const P = TUNING.entities.pursuit;
  a.timer2 -= w.dt;

  if (a.timer2 <= 0) {
    a.timer2 = a.rng.range(P.sweepInterval[0], P.sweepInterval[1]);
    const audible = live ?? (!w.player.hidden && w.player.noise >= TUNING.entities.noiseThreshold);

    if (audible) {
      const d = a.state.distToPlayer;
      const err = Math.min(P.sweepErrorMax, Math.max(P.sweepErrorMin, d * P.sweepError));
      const spot =
        randomWalkableNear(w.grid, a.rng, w.player.pos, err * 0.45, err, 12, w.props, a.def.radius) ??
        randomWalkableNear(w.grid, a.rng, w.player.pos, 1, err, 8, w.props, a.def.radius);
      if (spot) {
        a.dest.x = spot.x;
        a.dest.z = spot.z;
      }
    } else {
      // Nothing to follow. Cast about locally rather than hold a stale bearing
      // — a hunter that keeps walking to where the player was a minute ago
      // arrives at an empty room, which is the correct outcome.
      const spot = randomWalkableNear(w.grid, a.rng, a.state.pos, 6, 18, 12, w.props, a.def.radius);
      if (spot) {
        a.dest.x = spot.x;
        a.dest.z = spot.z;
      }
    }
    // The anchor follows, so that whatever the behaviour does with it later
    // (a smiler's dark spot, a wretch's corridor) starts from where it is now.
    a.anchor.x = a.dest.x;
    a.anchor.z = a.dest.z;
  }

  if (dist2(a.state.pos, a.dest) <= ARRIVE2) {
    // Arrived and found nothing. Take the next bearing early rather than stand
    // in an empty room for the rest of the interval.
    a.timer2 = Math.min(a.timer2, 1.2);
    holdStill(a);
    return;
  }

  moveTo(a, w, a.dest, speed);
}
